import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router } from "react-router-dom";
import "./index.css";
import { Amplify } from "aws-amplify";
import AuthProvider from "./Auth/AuthProvider";
import { captureSsoEntry } from "./Auth/ssoSession";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-locize-backend";

import "react-toastify/dist/ReactToastify.css";
import { ApolloProvider, ApolloClient, InMemoryCache } from "@apollo/client";
import { Settings } from "luxon";
import { QueryProvider } from "./API/QueryProvider";
import { profilesCacheFieldPolicy } from "./AppSettings/Permissions/PermissionsApi";

// Pin luxon's default locale so toRelative()/toFormat() don't pick up the
// browser locale (moment's default was always English). This preserves the
// moment-era behavior for users on non-en browsers (he, fr-FR, de-DE, etc.)
// and protects any future luxon call sites from regressing.
Settings.defaultLocale = "en-US";

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .use(LanguageDetector)
  .use(Backend)
  .init({
    debug: true,
    backend: {
      projectId: import.meta.env.VITE_LOCIZE_I18_PROJECT_ID,
      apiKey: import.meta.env.VITE_LOCIZE_I18_API_KEY,
      referenceLng: "en-US",
    },
    ns: "seemplicity",
    defaultNS: "seemplicity",
    saveMissing: true,
    lng: "en-US",
    fallbackLng: "en-US",
    interpolation: {
      escapeValue: false,
    },
  });

const client = new ApolloClient({
  // link is intentionally an empty array; the link chain is wired later in
  // AppRouter via client.setLink(). Cast keeps the empty-array initializer
  // while satisfying ApolloClient's ApolloLink-typed `link` option.
  link: [] as unknown as ApolloClient<unknown>["link"],
  connectToDevTools: true,
  cache: new InMemoryCache({
    typePolicies: {
      KeyValuePair: { keyFields: ["id", "name"] },
      DatasourceDefinition: { keyFields: ["id", "name"] },
      Datasource: { keyFields: ["id", "name"] },
      Query: {
        fields: {
          users: {
            merge(existing, incoming) {
              return {
                edges: [...incoming?.edges],
              };
            },
          },
          profiles: profilesCacheFieldPolicy,
          preferences: {
            merge(existing, incoming) {
              return { ...existing, ...incoming };
            },
          },
          remediation_items_view: {
            keyArgs: [
              "filters_config",
              "sort",
              "after",
              "first",
              "with_total_count",
            ],
          },
        },
      },
    },
  }),
});

// Allow non-production environments to set the redirect domain as they want.
const redirectDomain =
  import.meta.env.VITE_ENV !== "production"
    ? `${window.location.protocol}//${window.location.host}`
    : import.meta.env.VITE_DOMAIN;

const config = {
  aws_project_region: import.meta.env.VITE_COGNITO_REGION,
  aws_cognito_identity_pool_id: import.meta.env.VITE_COGNITO_IDENTITY_POOL_ID,
  aws_cognito_region: import.meta.env.VITE_COGNITO_REGION,
  aws_user_pools_id: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  aws_user_pools_web_client_id: import.meta.env.VITE_COGNITO_WEB_CLIENT_ID,
  oauth: {
    domain: `${import.meta.env.VITE_AUTH_DOMAIN}`,
    scope: ["email", "openid"],
    redirectSignIn: redirectDomain,
    redirectSignOut: `${redirectDomain}/logout`,
    responseType: "code",
  },
};

// For a hosted-UI user with an expired refresh token, Amplify's
// cleanUpInvalidSession redirects the whole tab to the Cognito hosted-UI
// /logout endpoint, destroying a /saml/<provider> URL before AuthSAML can
// mount. Capture it here: this runs before Amplify.configure arms the OAuth
// response handler, and long before AppRouter's bootstrap effect calls
// Auth.currentAuthenticatedUser().
captureSsoEntry(window.location.pathname, window.location.search);

Amplify.configure(config);

const container = document.getElementById("root");
const root = createRoot(container!);
root.render(
  <>
    <ApolloProvider client={client}>
      <QueryProvider>
        <Router>
          <AuthProvider />
        </Router>
      </QueryProvider>
    </ApolloProvider>
  </>
);
